import { useState, useEffect, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Modal, Badge } from 'react-bootstrap';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import Select from 'react-select';

import { buscarUsuarios, limpiarUsuarios } from '../../actions/usuarios';
import { detallesSolicitudWA, limpiarSolicitudWA, asignarSolicitudWA, cambiarStatusSolicitudWA, convertirSolicitudWA } from '../../actions/solicitudes_wa';

import { CustomValueContainer } from '../../components/react_select/ReactSelect';

const DetallesSolicitudWA = ({ solicitud, reestablecerSolicitud }) => {

    const dispatch = useDispatch();

    const { usuario } = useSelector(state => state.auth);
    const { detalles } = useSelector(state => state.solicitudes_wa);
    const { listado: listadoUsuarios } = useSelector(state => state.usuarios);

    const [detallesModal, setDetallesModal] = useState(false);
    const [statusProcesar, setStatusProcesar] = useState(false);

    const [asesorData, setAsesorData] = useState(null);
    const [asesorSeleccionado, setAsesorSeleccionado] = useState(null); // objeto completo para el reducer
    const [asesoresFilter, setAsesoresFilter] = useState([]);
    const [asesorPredilecto, setAsesorPredilecto] = useState(null);

    const styles_select = {
        menuPortal: base => ({ ...base, zIndex: 9999 }),
        control: (provided) => ({
            ...provided,
            paddingTop: '5px',
            paddingBottom: '5px',
        }),
        valueContainer: (provided) => ({
            ...provided,
            overflow: 'visible',
            marginTop: '10px'
        }),
        placeholder: (provided, state) => ({
            ...provided,
            position: 'absolute',
            top: state.hasValue || state.selectProps.inputValue ? -15 : '0%',
            transition: 'top 0.1s, font-size 0.1s',
            fontSize: (state.hasValue || state.selectProps.inputValue) && 13
        })
    };

    /* Abrir */
    const handleAbrir = () => {
        if (solicitud?._id) {
            dispatch(detallesSolicitudWA(solicitud._id));
        }
        if (usuario?.tipo == 0) {
            dispatch(buscarUsuarios({
                id: null,
                nombre: null,
                apellido_paterno: null,
                apellido_materno: null,
                tipo: null,
                fecha_inicio: null,
                fecha_fin: null
            }));
        }
    }

    /* Cerrar */
    const handleCerrar = () => {
        if (!statusProcesar) {
            setAsesorData(null);
            setAsesorSeleccionado(null);
            setAsesoresFilter([]);
            setAsesorPredilecto(null);
            dispatch(limpiarSolicitudWA());
            dispatch(limpiarUsuarios());
            reestablecerSolicitud(null);
            setDetallesModal(false);
        }
    }

    /* Asignar asesor desde select */
    const handleSelectAsesor = (option) => {
        setAsesorData(option);
        if (option) {
            // guardamos el objeto completo del listado para que el reducer
            // pueda actualizar la fila con nombre y apellido_paterno correctos
            const usuarioCompleto = listadoUsuarios.find(u => u._id === option.value) || null;
            setAsesorSeleccionado(usuarioCompleto);
        } else {
            setAsesorSeleccionado(null);
        }
    }

    const handleAsignar = async () => {
        if (!asesorSeleccionado) return;
        await setStatusProcesar(true);
        await dispatch(asignarSolicitudWA({ id: detalles._id, usuario: asesorSeleccionado }));
        await setStatusProcesar(false);
    }

    /* Asignarme a mí — pasamos el objeto completo del usuario autenticado */
    const handleAsignarme = async () => {
        await setStatusProcesar(true);
        await dispatch(asignarSolicitudWA({ id: detalles._id, usuario }));
        await setStatusProcesar(false);
    }

    /* Cambiar status */
    const handleCambiarStatus = async () => {
        const nuevoStatus = detalles?.status === 0 ? 1 : 0;
        await setStatusProcesar(true);
        await dispatch(cambiarStatusSolicitudWA({ id: detalles._id, status: nuevoStatus }));
        await setStatusProcesar(false);
    }

    /* Convertir en asesoría */
    const handleConvertir = async () => {
        await setStatusProcesar(true);
        await dispatch(convertirSolicitudWA(detalles._id));
        await setStatusProcesar(false);
    }

    /* Rechazar solicitud */
    const handleRechazar = async () => {
        await setStatusProcesar(true);
        await dispatch(cambiarStatusSolicitudWA({ id: detalles._id, status: 2 }));
        await setStatusProcesar(false);
    }

    /* Helpers */
    const nombreCompleto = (persona) => {
        if (!persona) return null;
        return [persona.nombre, persona.apellido_paterno, persona.apellido_materno].filter(Boolean).join(' ');
    }

    const formatearFechaHora = (fecha) => {
        if (!fecha) return '';
        return new Date(fecha).toLocaleString('es-MX', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    }

    /* Effects */
    useEffect(() => {
        if (solicitud) {
            setDetallesModal(true);
            setAsesorPredilecto(solicitud.cliente?.asesor || null);
        }
    }, [solicitud]);

    useEffect(() => {
        if (detalles?.usuario) {
            const u = detalles.usuario;
            setAsesorData({ value: u._id, label: nombreCompleto(u) });
            setAsesorSeleccionado(u);
        } else {
            setAsesorData(null);
            setAsesorSeleccionado(null);
        }
    }, [detalles]);

    useEffect(() => {
        const options = listadoUsuarios?.map(u => ({
            value: u._id,
            label: nombreCompleto(u),
            clientes_asignados: u.clientes_asignados ?? 0,
            recomendado: asesorPredilecto?._id && u._id?.toString() === asesorPredilecto._id?.toString()
        })) || [];

        options.sort((a, b) => (b.recomendado ? 1 : 0) - (a.recomendado ? 1 : 0));

        setAsesoresFilter(options);
    }, [listadoUsuarios, asesorPredilecto]);

    return (
        <Fragment>
            <HelmetProvider>
                <Helmet>
                    <style>{`
                        .chat-container {
                            display: flex;
                            flex-direction: column;
                            gap: 8px;
                            max-height: 400px;
                            overflow-y: auto;
                            padding: 12px;
                            background: #ece5dd;
                            border-radius: 8px;
                        }
                        .chat-wa-row {
                            display: flex;
                        }
                        .chat-wa-row.bot {
                            justify-content: flex-start;
                        }
                        .chat-wa-row.usuario {
                            justify-content: flex-end;
                        }
                        .chat-wa-bubble {
                            max-width: 72%;
                            padding: 8px 12px;
                            border-radius: 8px;
                            font-size: 0.9rem;
                            line-height: 1.4;
                            word-break: break-word;
                        }
                        .chat-wa-row.bot .chat-wa-bubble {
                            background: #fff;
                            border-top-left-radius: 0;
                        }
                        .chat-wa-row.usuario .chat-wa-bubble {
                            background: #dcf8c6;
                            border-top-right-radius: 0;
                        }
                        .chat-wa-time {
                            font-size: 0.7rem;
                            color: #888;
                            text-align: right;
                            margin-top: 3px;
                        }
                    `}</style>
                </Helmet>
            </HelmetProvider>

            <Modal
                size="xl"
                show={detallesModal}
                backdrop="static"
                centered
                onEntered={() => handleAbrir()}
                onHide={() => handleCerrar()}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Solicitud de WhatsApp</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {/* Metadata */}
                    <div className="row mb-3">
                        <div className="col-md-3">
                            <label className="fw-bold text-muted" style={{ fontSize: '0.8rem' }}>Número</label>
                            <p className="mb-1">{(detalles?.wa_id || solicitud?.wa_id) ? <>{detalles?.wa_id || solicitud?.wa_id} <a href={`https://wa.me/${(detalles?.wa_id || solicitud?.wa_id).replace(/\D/g, '')}`} target="_blank" rel="noreferrer"><i className="fab fa-whatsapp text-success"></i></a></> : '—'}</p>
                        </div>
                        <div className="col-md-3">
                            <label className="fw-bold text-muted" style={{ fontSize: '0.8rem' }}>Nombre WA</label>
                            <p className="mb-1">{detalles?.nombre_wa || solicitud?.nombre_wa || '—'}</p>
                        </div>
                        <div className="col-md-3">
                            <label className="fw-bold text-muted" style={{ fontSize: '0.8rem' }}>Cliente</label>
                            <p className="mb-1">
                                {
                                    detalles?.cliente
                                        ? <Link to={`/clientes/detalles/${detalles.cliente._id}`} onClick={() => handleCerrar()}>
                                            {nombreCompleto(detalles.cliente)}
                                          </Link>
                                        : <Badge bg="secondary">Número desconocido</Badge>
                                }
                            </p>
                        </div>
                        <div className="col-md-3">
                            <label className="fw-bold text-muted" style={{ fontSize: '0.8rem' }}>Fecha</label>
                            <p className="mb-1">{formatearFechaHora(detalles?.fecha || solicitud?.fecha)}</p>
                        </div>
                    </div>

                    <hr />

                    {/* Sección de asignación */}
                    {
                        detalles?.status === 0 && (usuario?.tipo == 0 || !detalles?.usuario) &&
                            <div className="row mb-3 align-items-end">
                                {
                                    usuario?.tipo == 0 &&
                                        <>
                                            <div className="col-md-7 form-group">
                                                <div className="input-group h-100">
                                                    <Select
                                                        id="asesor"
                                                        name="asesor"
                                                        className="select-group"
                                                        classNamePrefix="react-select"
                                                        placeholder="Asesor"
                                                        isClearable={true}
                                                        options={asesoresFilter}
                                                        value={asesorData}
                                                        onChange={(value) => handleSelectAsesor(value)}
                                                        menuPortalTarget={document.body}
                                                        components={{ ValueContainer: CustomValueContainer }}
                                                        styles={{ ...styles_select }}
                                                        formatOptionLabel={(option) => (
                                                            <span className="d-flex align-items-center justify-content-between">
                                                                <span>
                                                                    {option.label}
                                                                    {option.recomendado && <Badge pill bg="success" className="ms-2" style={{ fontSize: '0.7rem' }}>Asesor recomendado</Badge>}
                                                                </span>
                                                                <span className="text-muted ms-3" style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                                                                    <i className="fas fa-users fa-xs me-1"></i>{option.clientes_asignados}
                                                                </span>
                                                            </span>
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                {
                                                    statusProcesar
                                                        ? <button type="button" className="btn btn-primary w-100" disabled>
                                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Asignar
                                                          </button>
                                                        : <button type="button" className="btn btn-primary w-100" onClick={() => handleAsignar()} disabled={!asesorSeleccionado}>
                                                            <i className="fas fa-user-check fa-sm"></i> Asignar
                                                          </button>
                                                }
                                            </div>
                                        </>
                                }
                                {
                                    !detalles?.usuario &&
                                        <div className={usuario?.tipo == 0 ? 'col-md-2' : 'col-md-12'}>
                                            {
                                                statusProcesar
                                                    ? <button type="button" className="btn btn-outline-primary w-100" disabled>
                                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                      </button>
                                                    : <button type="button" className="btn btn-outline-primary w-100" onClick={() => handleAsignarme()}>
                                                        <i className="fas fa-user fa-sm"></i> Asignarme
                                                      </button>
                                            }
                                        </div>
                                }
                            </div>
                    }

                    {/* Conversación */}
                    {
                        usuario?.permisos?.solicitudes?.detalles &&
                            <Fragment>
                                <p className="fw-bold mb-2">Conversación</p>
                                <div className="chat-container">
                                    {
                                        detalles?.contexto?.length > 0
                                            ? detalles.contexto.map((msg, i) => (
                                                <div key={i} className={`chat-wa-row ${msg.autor}`}>
                                                    <div className="chat-wa-bubble">
                                                        {msg.body}
                                                        <div className="chat-wa-time">{formatearFechaHora(msg.fecha)}</div>
                                                    </div>
                                                </div>
                                            ))
                                            : <p className="text-center text-muted my-3">Sin mensajes registrados</p>
                                    }
                                </div>
                            </Fragment>
                    }
                </Modal.Body>

                <Modal.Footer>
                    {
                        usuario?.permisos?.solicitudes?.status && detalles?.status === 0 && detalles?.usuario &&
                            (
                                statusProcesar
                                    ? <>
                                        <button type="button" className="btn btn-secondary me-auto" disabled>
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        </button>
                                      </>
                                    : <>
                                        <button type="button" className="btn btn-success me-auto" onClick={() => handleConvertir()}>
                                            <i className="fas fa-file-alt fa-sm"></i> Convertir en asesoría
                                        </button>
                                        <button type="button" className="btn btn-outline-danger me-2" onClick={() => handleRechazar()}>
                                            <i className="fas fa-ban fa-sm"></i> Rechazar
                                        </button>
                                      </>
                            )
                    }
                    <button type="button" className="btn btn-danger" onClick={() => handleCerrar()} disabled={statusProcesar}>
                        <i className="fas fa-times fa-sm"></i> Cerrar
                    </button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    )
}

export default DetallesSolicitudWA;
